import { Box, Button, IconButton } from '@wix/design-system';
import { ArrowUp } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, { useContext } from 'react';

import { RicosContext } from './../../../ricos-context';

import {
  AI_MODAL_CANCEL_BUTTON,
  AI_MODAL_PROMPT_INPUT,
  AI_MODAL_SUBMIT_BUTTON,
} from '../../data-hooks';
import type { PromptInputAreaRef } from '../../prompt-input-area/prompt-input-area';
import { PromptInputArea } from '../../prompt-input-area/prompt-input-area';
import styles from './textual-prompt-input-area.rtlignore-scss';

type TextualPromptInputAreaProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  maxLength: number;
  zIndex: number;
  busy?: boolean;
  handleTextAreaEnter?: boolean;
  placeholder?: string;
  isSubmitButtonVisible?: () => boolean;
};

export type TextualPromptInputAreaRef = {
  focus: () => void;
};

export const TextualPromptInputArea = React.forwardRef<
  TextualPromptInputAreaRef,
  TextualPromptInputAreaProps
>(
  (
    {
      value,
      onChange,
      maxLength,
      onSubmit,
      handleTextAreaEnter,
      busy,
      onCancel,
      zIndex,
      placeholder,
      isSubmitButtonVisible,
    },
    ref
  ) => {
    const { t } = useContext(RicosContext);
    const inputRef = React.useRef<PromptInputAreaRef>(null);

    React.useImperativeHandle(
      ref,
      () => {
        return {
          focus() {
            inputRef.current?.focus();
          },
        };
      },
      []
    );

    React.useEffect(() => {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }, []);

    const isVisibleSubmit = isSubmitButtonVisible
      ? isSubmitButtonVisible() && !busy
      : !busy && value.length;

    return (
      <Box>
        <PromptInputArea
          ref={inputRef}
          busy={busy}
          placeholder={t(placeholder ?? 'AIPlugin_Input_Placeholder_Text')}
          maxLength={maxLength}
          onChange={onChange}
          value={value}
          busyValue={t('AIPlugin_Input_Loading_Text')}
          onCancel={() => onCancel()}
          onSubmit={() => {
            if (handleTextAreaEnter) {
              onSubmit(value);
            }
          }}
          zIndex={zIndex}
          dataHook={AI_MODAL_PROMPT_INPUT}
        />
        <Box
          className={classNames(styles.buttonWrapper, {
            [styles.visibleSubmit]: isVisibleSubmit,
            [styles.visibleCancel]: busy,
          })}
        >
          <Box flexGrow={1} marginLeft="SP2" verticalAlign="middle">
            <Button
              dataHook={AI_MODAL_CANCEL_BUTTON}
              disabled={!busy}
              className={styles.cancelButton}
              size="small"
              priority="secondary"
              onClick={onCancel}
            >
              {t('AIPlugin_Button_Stop_Text')}
            </Button>
            <IconButton
              dataHook={AI_MODAL_SUBMIT_BUTTON}
              disabled={busy}
              className={styles.submitButton}
              size="small"
              onClick={() => onSubmit(value)}
              tabIndex={isVisibleSubmit ? 0 : -1}
            >
              <ArrowUp />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }
);
